import { Api } from "metabase/api";
import { idTag, invalidateTags, listTag, tag } from "metabase/api/tags";
import {
  type CreateGitHubAppRequest,
  type CreateGitHubAppResponse,
  type ExportGitSyncRequest,
  type GitSyncChange,
  type GitSyncChangesResponse,
  type GitSyncCollection,
  type GitSyncConnectionCheck,
  type GitSyncDatabase,
  type GitSyncSettings,
  type GitSyncStatus,
  type GitSyncTask,
  type ImportGitSyncRequest,
  type OpenGitSyncPullRequestRequest,
  type RemoteSyncChangesResponse,
  type RemoteSyncEntity,
  type TestGitSyncConnectionRequest,
  type UpdateGitSyncCollectionRequest,
  type UpdateGitSyncDatabaseRequest,
  type UpdateGitSyncPermissionsRequest,
  type UpdateGitSyncSettingsRequest,
  type UpdateGitSyncSettingsResponse,
  isCardDisplayType,
} from "metabase-types/api";

const SETTINGS_TAG = idTag("git-sync", "settings");
const STATUS_TAG = idTag("git-sync", "status");
const CHANGES_TAG = idTag("git-sync", "changes");
const COLLECTIONS_TAG = idTag("git-sync", "collections");
const DATABASES_TAG = idTag("git-sync", "databases");

/** The tags of what a finished import may have changed. */
export const GIT_SYNC_CONTENT_TAGS = [
  STATUS_TAG,
  CHANGES_TAG,
  COLLECTIONS_TAG,
  DATABASES_TAG,
  tag("collection"),
  tag("collection-tree"),
  tag("card"),
  tag("dashboard"),
  tag("document"),
  tag("snippet"),
  tag("timeline"),
  tag("action"),
  tag("table"),
  tag("field"),
  tag("segment"),
  tag("measure"),
  tag("permissions-group"),
];

const toRemoteSyncEntity = ({
  id,
  name,
  model,
  collection_id,
  display,
  sync_status,
}: GitSyncChange): RemoteSyncEntity => ({
  id,
  name: name ?? "",
  model,
  collection_id: collection_id ?? undefined,
  display: isCardDisplayType(display) ? display : undefined,
  sync_status,
});

export const gitSyncApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getGitSyncSettings: builder.query<GitSyncSettings, void>({
      query: () => ({
        method: "GET",
        url: "/api/remote-sync/settings",
      }),
      providesTags: [SETTINGS_TAG],
    }),
    updateGitSyncSettings: builder.mutation<
      UpdateGitSyncSettingsResponse,
      UpdateGitSyncSettingsRequest
    >({
      query: (body) => ({
        method: "PUT",
        url: "/api/remote-sync/settings",
        body,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          SETTINGS_TAG,
          STATUS_TAG,
          CHANGES_TAG,
          tag("session-properties"),
        ]),
    }),
    disconnectGitSync: builder.mutation<GitSyncSettings, void>({
      query: () => ({
        method: "DELETE",
        url: "/api/remote-sync/settings",
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          SETTINGS_TAG,
          STATUS_TAG,
          CHANGES_TAG,
          tag("session-properties"),
        ]),
    }),
    createGitHubAppManifest: builder.mutation<
      CreateGitHubAppResponse,
      CreateGitHubAppRequest
    >({
      query: (body) => ({
        method: "POST",
        url: "/api/remote-sync/github-app/manifest",
        body,
      }),
    }),
    testGitSyncConnection: builder.mutation<
      GitSyncConnectionCheck,
      TestGitSyncConnectionRequest
    >({
      query: (body) => ({
        method: "POST",
        url: "/api/remote-sync/test-connection",
        body,
      }),
    }),
    getGitSyncStatus: builder.query<GitSyncStatus, void>({
      query: () => ({
        method: "GET",
        url: "/api/remote-sync/status",
      }),
      providesTags: [STATUS_TAG],
    }),
    getGitSyncChanges: builder.query<RemoteSyncChangesResponse, void>({
      query: () => ({
        method: "GET",
        url: "/api/remote-sync/changes",
      }),
      transformResponse: ({
        dirty,
        changed_collections,
      }: GitSyncChangesResponse): RemoteSyncChangesResponse => ({
        dirty: dirty.map(toRemoteSyncEntity),
        changedCollections: changed_collections,
      }),
      providesTags: [CHANGES_TAG],
    }),
    exportGitSync: builder.mutation<GitSyncTask, ExportGitSyncRequest>({
      query: (body) => ({
        method: "POST",
        url: "/api/remote-sync/export",
        body,
      }),
      invalidatesTags: (_, error) => invalidateTags(error, [STATUS_TAG]),
    }),
    openGitSyncPullRequest: builder.mutation<
      GitSyncTask,
      OpenGitSyncPullRequestRequest
    >({
      query: (body) => ({
        method: "POST",
        url: "/api/remote-sync/pull-request",
        body,
      }),
      invalidatesTags: (_, error) => invalidateTags(error, [STATUS_TAG]),
    }),
    importGitSync: builder.mutation<GitSyncTask, ImportGitSyncRequest>({
      query: (body) => ({
        method: "POST",
        url: "/api/remote-sync/import",
        body,
      }),
      invalidatesTags: (_, error) => invalidateTags(error, [STATUS_TAG]),
    }),
    listGitSyncCollections: builder.query<GitSyncCollection[], void>({
      query: () => ({
        method: "GET",
        url: "/api/remote-sync/collections",
      }),
      providesTags: [COLLECTIONS_TAG],
    }),
    updateGitSyncCollection: builder.mutation<
      GitSyncCollection,
      UpdateGitSyncCollectionRequest
    >({
      query: ({ id, synced }) => ({
        method: "PUT",
        url: `/api/remote-sync/collection/${id}`,
        body: { synced },
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [
          COLLECTIONS_TAG,
          CHANGES_TAG,
          listTag("collection"),
          tag("collection"),
          tag("collection-tree"),
        ]),
    }),
    listGitSyncDatabases: builder.query<GitSyncDatabase[], void>({
      query: () => ({
        method: "GET",
        url: "/api/remote-sync/databases",
      }),
      providesTags: [DATABASES_TAG],
    }),
    updateGitSyncDatabase: builder.mutation<
      GitSyncDatabase,
      UpdateGitSyncDatabaseRequest
    >({
      query: ({ id, synced }) => ({
        method: "PUT",
        url: `/api/remote-sync/database/${id}`,
        body: { synced },
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [DATABASES_TAG, CHANGES_TAG]),
    }),
    updateGitSyncPermissions: builder.mutation<
      GitSyncSettings,
      UpdateGitSyncPermissionsRequest
    >({
      query: (body) => ({
        method: "PUT",
        url: "/api/remote-sync/permissions",
        body,
      }),
      invalidatesTags: (_, error) =>
        invalidateTags(error, [SETTINGS_TAG, CHANGES_TAG]),
    }),
  }),
});

export const {
  useGetGitSyncSettingsQuery,
  useUpdateGitSyncSettingsMutation,
  useDisconnectGitSyncMutation,
  useTestGitSyncConnectionMutation,
  useCreateGitHubAppManifestMutation,
  useGetGitSyncStatusQuery,
  useGetGitSyncChangesQuery,
  useExportGitSyncMutation,
  useOpenGitSyncPullRequestMutation,
  useImportGitSyncMutation,
  useListGitSyncCollectionsQuery,
  useUpdateGitSyncCollectionMutation,
  useListGitSyncDatabasesQuery,
  useUpdateGitSyncDatabaseMutation,
  useUpdateGitSyncPermissionsMutation,
} = gitSyncApi;
